import { Box, Flex, TextLink } from '@weareberyl/design-system'
import { Card } from 'antd'

import { addMinutes, parseISO, subMinutes } from 'date-fns'
import * as routes from '~/constants/routes'
import {
  capitalize,
  formatDatetime,
  formatJourneyInitiator,
  getLogsLink,
  isDockedScheme,
} from '../../utils'
import { EndJourneyButton } from './actions'

import CardSection from '~/components/shared/CardSection'
import { JourneyRewards } from './Rewards'

import { PARKING_DESCRIPTION } from '~/constants/domain'
import {
  JourneySetQuery_journey_set_JourneySet_journeys_Journey_possible_rewards_VehicleReward as ConfirmedRewards,
  JourneyInitiator,
  JourneySetQuery_journey_set_JourneySet_journeys_Journey_confirmed_rewards_VehicleReward as PossibleRewards,
  useJourneySummaryFragment,
} from '~/gql/generated/graphql'
import IconHeading from '../Utils/IconHeading'
import MaybeLink from '../Utils/MaybeLink'
import JourneyPhotoModal from './JourneyPhotoModal'
import { IDProps } from './Set'

const logsLink = (
  journeyId: string,
  moduleId: string,
  startedAt: string,
  completedAt: string,
) => {
  // Calculate the start and end filter times with 3 mins extra each side, in case clocks are a bit skewed
  const filterStart = subMinutes(parseISO(startedAt), 3)
  const today = new Date()
  const filterEnd = completedAt ? addMinutes(parseISO(completedAt), 3) : today
  return getLogsLink(
    `(@journey_id:${journeyId} OR @module_id:${moduleId} OR @variables.moduleId:${moduleId})`,
    filterStart,
    filterEnd,
  )
}

const JourneySummary = ({ id, withLink }: IDProps & { withLink?: boolean }) => {
  const { data: journey } = useJourneySummaryFragment({ id })

  if (!journey?.id) {
    return null
  }
  const hasDistance = journey.distance_in_km && journey.distance_in_mi
  const hasSpeed = journey.speed_in_km && journey.speed_in_mi
  const dockedScheme = journey.scheme && isDockedScheme(journey.scheme.id)
  const hasVehicleDocked = dockedScheme && journey?.vehicle_docked !== null
  const hasDocksAvailable = dockedScheme && journey?.docks_available !== null
  const historyReady = journey.state?.message === 'history_ready'
  const parkingStatus = journey.parking_status
    ? (PARKING_DESCRIPTION[journey.parking_status] ?? 'Not implemented')
    : 'Unknown'
  const vehicleLockState = capitalize(journey.vehicle_lock_state) ?? 'Unknown'
  const batteryStart = journey?.start_vehicle_battery_percent ?? null
  const batteryEnd = journey?.end_vehicle_battery_percent ?? null

  const primaryCardItems = [
    { key: 'State', value: capitalize(journey.state?.message) },
    { key: 'Frame number', value: journey.vehicle?.frame_id ?? '-' },
  ]
  if (journey.duration !== null) {
    primaryCardItems.push({
      key: 'Duration',
      value: `${journey.duration} mins`,
    })
  }
  if (historyReady) {
    primaryCardItems.push(
      {
        key: 'Parking',
        value: parkingStatus,
      },
      { key: 'Lock state', value: vehicleLockState },
    )
  }
  if (journey.end_in_zone) {
    if (hasVehicleDocked) {
      primaryCardItems.push({
        key: 'Ended docked',
        value: journey.vehicle_docked ? 'Yes' : 'No',
      })
    }
    if (hasDocksAvailable) {
      primaryCardItems.push({
        key: 'Docks available in bay',
        value: journey.docks_available?.toString(),
      })
    }
  }
  if (typeof batteryStart === 'number') {
    primaryCardItems.push({
      key: 'Start battery',
      value: `${batteryStart.toString()}%`,
    })
  }

  if (typeof batteryEnd === 'number') {
    primaryCardItems.push({
      key: 'End battery',
      value: `${batteryEnd.toString()}%`,
    })
  }

  const secondaryCardItems = [
    { key: 'Last state change', value: formatDatetime(journey.state?.time) },
  ]
  if (hasDistance) {
    secondaryCardItems.push({
      key: 'Distance',
      value: `${journey.distance_in_km} km / ${journey.distance_in_mi} mi`,
    })
  }
  if (hasSpeed) {
    secondaryCardItems.push({
      key: 'Speed',
      value: `${journey.speed_in_km} km/h / ${journey.speed_in_mi} mph`,
    })
  }
  secondaryCardItems.push({
    key: 'Initiated by',
    value: formatJourneyInitiator(
      journey.initiator ?? JourneyInitiator.app,
      journey.initiator_app_name,
    ),
  })

  return (
    <Card>
      <Flex width="100%" mb={5} style={{ gap: 20 }}>
        <MaybeLink
          route={`${routes.JOURNEYS}/${id}`}
          schemeId={journey.scheme?.id}
          withLink={withLink}
        >
          <IconHeading iconType="bikeJourney" withLink={withLink}>
            Journey {journey.id}
          </IconHeading>
        </MaybeLink>
        <Box flex={2} maxWidth={160}>
          {!journey.is_complete && journey.scheme && (
            <Box mb={4}>
              <EndJourneyButton journeyId={journey.id} />
            </Box>
          )}
          <Flex justifyContent="flex-end">
            <TextLink
              variant="gray"
              onPress={() =>
                journey.id &&
                window.open(
                  logsLink(
                    journey.id,
                    journey.vehicle?.detail?.module?.id ?? '',
                    journey.started_at,
                    journey.completed_at,
                  ),
                  '_blank',
                )
              }
            >
              Server logs
            </TextLink>
          </Flex>
        </Box>
      </Flex>
      <Flex justifyContent="space-between" width="100%">
        <CardSection mr={5} items={primaryCardItems} />
        <CardSection items={secondaryCardItems} />
      </Flex>
      {journey.scheme?.config?.is_vehicle_rewards_enabled &&
        (journey.possible_rewards?.length ?? 0) > 0 && (
          <JourneyRewards
            journeyErrored={journey.is_error ?? false}
            journeyHistoryReady={historyReady}
            possibleRewards={
              (journey.possible_rewards as PossibleRewards[]) ?? []
            }
            confirmedRewards={
              (journey.confirmed_rewards as ConfirmedRewards[]) ?? []
            }
          />
        )}
      <JourneyPhotoModal journeyId={journey.id} />
    </Card>
  )
}

export default JourneySummary
